class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close()
    );

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener(
        'click',
        this.close.bind(this)
      );
    }

    this.setHeaderCartIconAccessibility();


    /* ==================================================
       SKINADDICT FREE SHIPPING PROGRESS
    ================================================== */

    this.saShippingProgress = 0;

    this.saShippingObserver = new MutationObserver(() => {
      this.animateShippingProgress();
    });

    this.saShippingObserver.observe(this, {
      childList: true,
      subtree: true
    });

    requestAnimationFrame(() => {
      this.animateShippingProgress();
    });
  }


  /* ==================================================
     HEADER CART ICON
  ================================================== */

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector(
      '#cart-icon-bubble'
    );

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener(
      'click',
      (event) => {
        event.preventDefault();

        this.open(cartLink);
      }
    );

    cartLink.addEventListener(
      'keydown',
      (event) => {
        if (
          event.code.toUpperCase() === 'SPACE'
        ) {
          event.preventDefault();

          this.open(cartLink);
        }
      }
    );
  }


  /* ==================================================
     OPEN CART DRAWER
  ================================================== */

  open(triggeredBy) {
    if (
      this.classList.contains('active')
    ) {
      return;
    }

    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote =
      this.querySelector(
        '[id^="Details-"] summary'
      );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(
        cartDrawerNote
      );
    }


    /*
      Small delay ensures Dawn's drawer
      animation always triggers correctly.
    */

    setTimeout(() => {
      this.classList.add(
        'animate',
        'active'
      );

      /*
        Re-run progress animation whenever
        the drawer becomes visible.
      */

      this.animateShippingProgress();
    });


    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector(
                '.drawer__inner-empty'
              )
            : document.getElementById(
                'CartDrawer'
              );

        const focusElement =
          this.querySelector(
            '.drawer__inner'
          ) ||
          this.querySelector(
            '.drawer__close'
          );

        if (
          containerToTrapFocusOn &&
          focusElement
        ) {
          trapFocus(
            containerToTrapFocusOn,
            focusElement
          );
        }
      },
      {
        once: true
      }
    );


    document.body.classList.add(
      'overflow-hidden'
    );


    /*
      Dawn view event
    */

    this.querySelector(
      'cart-drawer-items'
    )?.dispatchViewEvent();
  }


  /* ==================================================
     CLOSE CART DRAWER
  ================================================== */

  close() {
    this.classList.remove(
      'active'
    );

    removeTrapFocus(
      this.activeElement
    );

    document.body.classList.remove(
      'overflow-hidden'
    );
  }


  /* ==================================================
     ACCESSIBLE CART NOTE
  ================================================== */

  setSummaryAccessibility(
    cartDrawerNote
  ) {
    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );


    if (
      cartDrawerNote
        .nextElementSibling
        ?.getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote
          .nextElementSibling
          .id
      );
    }


    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        event.currentTarget.setAttribute(
          'aria-expanded',
          !event.currentTarget
            .closest('details')
            .hasAttribute('open')
        );
      }
    );


    cartDrawerNote.parentElement
      ?.addEventListener(
        'keyup',
        onKeyUpEscape
      );
  }


  /* ==================================================
     RENDER CART CONTENT
  ================================================== */

  renderContents(parsedState) {

    const drawerInner =
      this.querySelector(
        '.drawer__inner'
      );

    if (
      drawerInner &&
      drawerInner.classList.contains(
        'is-empty'
      )
    ) {
      drawerInner.classList.remove(
        'is-empty'
      );
    }


    this.productId =
      parsedState.id;


    this.getSectionsToRender()
      .forEach((section) => {

        const sectionElement =
          section.selector
            ? document.querySelector(
                section.selector
              )
            : document.getElementById(
                section.id
              );


        if (!sectionElement) return;


        sectionElement.innerHTML =
          this.getSectionInnerHTML(
            parsedState.sections[
              section.id
            ],
            section.selector
          );
      });


    /*
      Reconnect overlay click after Dawn
      replaces the drawer HTML.
    */

    setTimeout(() => {

      const overlay =
        this.querySelector(
          '#CartDrawer-Overlay'
        );

      if (overlay) {
        overlay.addEventListener(
          'click',
          this.close.bind(this)
        );
      }


      /*
        Animate updated shipping progress
        before opening the drawer.
      */

      this.animateShippingProgress();


      this.open();

    });
  }


  /* ==================================================
     GET SECTION INNER HTML
  ================================================== */

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const parsedHTML =
      new DOMParser()
        .parseFromString(
          html,
          'text/html'
        );


    const selectedElement =
      parsedHTML.querySelector(
        selector
      );


    return selectedElement
      ? selectedElement.innerHTML
      : '';
  }


  /* ==================================================
     SECTIONS TO RENDER
  ================================================== */

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },

      {
        id: 'cart-icon-bubble'
      }
    ];
  }


  /* ==================================================
     GET SECTION DOM
  ================================================== */

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(
        selector
      );
  }


  /* ==================================================
     ACTIVE ELEMENT
  ================================================== */

  setActiveElement(element) {
    this.activeElement =
      element;
  }


  /* ==================================================
     SKINADDICT SHIPPING PROGRESS
  ================================================== */

  animateShippingProgress() {

    const bar =
      this.querySelector(
        '[data-sa-shipping-bar]'
      );


    /*
      Cart may be empty or feature disabled.
    */

    if (!bar) {
      this.saShippingProgress = 0;

      return;
    }


    let target =
      parseFloat(
        bar.dataset.progress || 0
      );


    if (
      Number.isNaN(target)
    ) {
      target = 0;
    }


    target =
      Math.max(
        0,
        Math.min(
          100,
          target
        )
      );


    /*
      Previous position allows the bar
      to animate both forwards and backwards.
    */

    let previous =
      parseFloat(
        this.saShippingProgress || 0
      );


    if (
      Number.isNaN(previous)
    ) {
      previous = 0;
    }


    previous =
      Math.max(
        0,
        Math.min(
          100,
          previous
        )
      );


    /*
      Set current position instantly.
    */

    bar.style.transition =
      'none';

    bar.style.width =
      `${previous}%`;


    /*
      Force browser layout recalculation.
      Needed so transition triggers reliably.
    */

    void bar.offsetWidth;


    /*
      Animate to new amount.
    */

    requestAnimationFrame(() => {

      bar.style.transition =
        'width .65s cubic-bezier(.22, 1, .36, 1)';

      bar.style.width =
        `${target}%`;


      this.saShippingProgress =
        target;

    });
  }


  /* ==================================================
     CLEANUP
  ================================================== */

  disconnectedCallback() {

    if (
      this.saShippingObserver
    ) {
      this.saShippingObserver.disconnect();
    }
  }
}


customElements.define(
  'cart-drawer',
  CartDrawer
);



/* ====================================================
   CART DRAWER ITEMS
==================================================== */

class CartDrawerItems extends CartItems {

  getSectionsToRender() {

    return [

      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector:
          '.drawer__inner'
      },


      {
        id: 'cart-icon-bubble',
        section:
          'cart-icon-bubble',
        selector:
          '.shopify-section'
      }

    ];
  }

}


customElements.define(
  'cart-drawer-items',
  CartDrawerItems
);